import { Navbar, Footer } from '@/components/marketing';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Store, Utensils, ShoppingBag, Pill, Laptop, Wrench } from 'lucide-react';

const solutions = [
  {
    icon: Store,
    title: 'Retail Stores',
    description: 'Perfect for electronics, clothing, and general retail shops looking to sell on WhatsApp.',
    benefits: ['Product catalog sharing', 'Quick order processing', 'Inventory sync', 'Customer loyalty'],
  },
  {
    icon: Utensils,
    title: 'Restaurants & Cafes',
    description: 'Take food orders via WhatsApp with menu sharing and automated order confirmations.',
    benefits: ['Digital menu', 'Order management', 'Delivery tracking', 'Review collection'],
  },
  {
    icon: ShoppingBag,
    title: 'Fashion & Apparel',
    description: 'Showcase your collection and handle customer inquiries about sizes, colors, and styles.',
    benefits: ['Product galleries', 'Size guides', 'Style recommendations', 'Return handling'],
  },
  {
    icon: Pill,
    title: 'Pharmacies',
    description: 'Handle prescription orders and medicine inquiries with compliance and care.',
    benefits: ['Prescription upload', 'Refill reminders', 'Drug information', 'Delivery scheduling'],
  },
  {
    icon: Laptop,
    title: 'Electronics',
    description: 'Manage complex product queries about specifications, compatibility, and warranties.',
    benefits: ['Spec comparisons', 'Warranty tracking', 'Tech support', 'Trade-in programs'],
  },
  {
    icon: Wrench,
    title: 'Services',
    description: 'Book appointments and manage service requests through WhatsApp conversations.',
    benefits: ['Appointment booking', 'Service quotes', 'Follow-ups', 'Feedback collection'],
  },
];

export default function SolutionsPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Solutions for{' '}
              <span className="gradient-text">Every Industry</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              SparkCommerce adapts to your business needs, whether you're running a retail store, 
              restaurant, or service business.
            </p>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution) => {
              const Icon = solution.icon;
              return (
                <div
                  key={solution.title}
                  className="p-6 rounded-2xl bg-card border border-border hover:border-primary/50 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{solution.title}</h3>
                  <p className="text-muted-foreground mb-4">{solution.description}</p>
                  <ul className="space-y-2">
                    {solution.benefits.map((benefit) => (
                      <li key={benefit} className="flex items-center gap-2 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Don't See Your Industry?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              SparkCommerce is flexible enough to work with any business. Contact us to learn 
              how we can customize the platform for your needs.
            </p>
            <Link to="/signup">
              <Button size="lg" className="rounded-full">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
